import { sendMessage, getConversation, submitFeedback } from "./utils/api-client";
import { getSessionId, clearSession } from "./utils/session-storage";
import { useState, useEffect } from "react";
import { cn } from "../utils/cn.ts";
import { PropertyPalHeader } from "./pp-header";
import { PropertyPalChatBody } from "./pp-chat-body";
import { PropertyPalInputSection } from "./pp-input-section";
import { PropertyPalBubble } from "./pp-bubble";
import { PropertyPalFeedbackWidget, type FeedbackData } from "./pp-feedback-widget";
import type { Feedback } from "./pp-feedback-buttons";
import { TrackEvent } from "../utils/analytics";

const componentContainer =
  "twp flex flex-col h-[624px] items-end gap-2.5 w-[320px] md:w-[380px] xl:w-[420px] fixed bottom-4 right-4 z-[9999]";
const innerContainer =
  "flex flex-col items-start self-stretch rounded-lg border-foggy-firth shadow-xl h-[624px] flex-1 min-h-0";

// Message type definition
type MessageType = "user" | "bot";
export interface ChatMessage {
  id: string;
  type: MessageType;
  text: string;
}

export type FeedbackStatus = "success" | "error" | undefined;

const initialBotMessage: ChatMessage = {
  id: crypto.randomUUID(),
  type: "bot",
  text: "👋 Hi there!\n\nBuying your first home can feel like a big step — I'm here to make things simpler.\n\nI can help with a range of topics, such as understanding the buying process, mortgages, legal aspects, or even finding a solicitor or mortgage advisor. Let me know what you'd like to focus on!",
};

export const PropertyPalLayout = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([initialBotMessage]);
  const [chatbotThinking, setChatbotThinking] = useState(false);
  const [feedbackType, setFeedbackType] = useState<Feedback>(undefined);
  const [feedbackStatus, setFeedbackStatus] = useState<FeedbackStatus>(undefined);

  // Handler to open/close the chatbot
  const handleOnClick = () => {
    if (!isOpen) {
      TrackEvent({ name: "property_pal_opened" });
    } else {
      TrackEvent({ name: "property_pal_closed" });
    }
    setIsOpen(!isOpen);
  };

  // Load conversation history on mount if session exists
  useEffect(() => {
    const loadConversation = async () => {
      const sessionId = getSessionId();
      if (!sessionId) return;

      try {
        const conversation = await getConversation(sessionId);
        if (conversation.messages.length > 0) {
          const mappedMessages: ChatMessage[] = conversation.messages.map((msg) => ({
            id: msg.messageId,
            type: msg.type === "input" ? "user" : "bot",
            text: msg.message,
          }));
          setMessages([initialBotMessage, ...mappedMessages]);
        }
      } catch {
        // If conversation fetch fails (expired/not found), just keep greeting
        clearSession();
      }
    };

    void loadConversation();
  }, []);

  // Handler for feedback button clicks
  const handleFeedbackChange = (feedback: Feedback) => {
    setFeedbackType(feedback);
    setFeedbackStatus(undefined);
  };

  // Handler for feedback widget close
  const handleFeedbackClose = () => {
    setFeedbackType(undefined);
  };

  // Handler for feedback submission
  const handleFeedbackSubmit = async (data: FeedbackData) => {
    const sessionId = getSessionId();
    if (!sessionId) return;

    try {
      await submitFeedback(sessionId, data.type, data.feedbackText || undefined);
      setFeedbackStatus("success");
      TrackEvent({ name: "property_pal_feedback_submitted" });
    } catch {
      setFeedbackStatus("error");
    } finally {
      setFeedbackType(undefined);
    }
  };

  // Handler to add a new user message and get bot response
  const handleSendMessage = async (text: string) => {
    TrackEvent({ name: "property_pal_message_sent" });

    // Clear any feedback status when sending a new message
    setFeedbackStatus(undefined);

    // Add user message
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), type: "user", text }]);
    setChatbotThinking(true);

    try {
      const response = await sendMessage(text);

      if (response.httpResponseCode == 200) {
        const data =
          response.response ?? "Sorry, there was a problem communicating with the Property Pal.  Please try again.";
        setMessages((prev) => [
          ...prev,
          {
            id: response.responseMessageId ?? crypto.randomUUID(),
            type: "bot",
            text: data,
          },
        ]);
      } else if (response.httpResponseCode == 429) {
        setMessages((prev) => [
          ...prev,
          {
            id: response.responseMessageId ?? crypto.randomUUID(),
            type: "bot",
            text: "You have reached the maximum number of messages allowed. Please try again later.",
          },
        ]);
      } else {
        const errorText = "Sorry, there was a problem communicating with the Property Pal.  Please try again.";
        setMessages((prev) => [
          ...prev,
          {
            id: response.responseMessageId ?? crypto.randomUUID(),
            type: "bot",
            text: errorText,
          },
        ]);
        return;
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          type: "bot",
          text: "Sorry, there was a problem communicating with the Property Pal.  Please try again.",
        },
      ]);
    } finally {
      setChatbotThinking(false);
    }
  };

  return (
    <div className={componentContainer}>
      <div className={cn(innerContainer, isOpen ? "visible" : "invisible")}>
        <PropertyPalHeader />
        <PropertyPalChatBody
          chatbotThinking={chatbotThinking}
          messages={messages}
          feedbackType={feedbackType}
          feedbackStatus={feedbackStatus}
          onFeedbackChange={handleFeedbackChange}
        />
        {feedbackType ? (
          <PropertyPalFeedbackWidget
            type={feedbackType}
            onClose={handleFeedbackClose}
            onSubmit={(data) => {
              void handleFeedbackSubmit(data);
            }}
          />
        ) : (
          <PropertyPalInputSection
            handleSendMessage={(text) => {
              void handleSendMessage(text);
            }}
          />
        )}
      </div>
      <PropertyPalBubble isOpen={isOpen} onClick={handleOnClick} />
    </div>
  );
};
